import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { useKnowledgeNoteMutations, useKnowledgeNotes } from "../hooks";
import type { KnowledgeNote } from "../types";

/** Baza wiedzy PRZ-ARON — osobna zakładka. Proste notatki tekstowe uczące AI
 *  wyceny, jak dobierać i wyceniać pozycje; aktywne notatki są doklejane do
 *  każdego promptu AI wyceny (odczyt pozycji, dopasowanie nakładów do cennika,
 *  wycena rynkowa, dobór podwykonawców, audyt). */
export function EstimateKnowledgePage() {
  const notes = useKnowledgeNotes();
  const { create, update, remove } = useKnowledgeNoteMutations();
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");

  const items = notes.data ?? [];
  const activeCount = items.filter((n) => n.is_active).length;

  function add() {
    const body = draft.trim();
    if (!body) return;
    create.mutate(body, { onSuccess: () => setDraft("") });
  }

  function startEdit(n: KnowledgeNote) {
    setEditingId(n.id);
    setEditBody(n.body);
  }

  function saveEdit() {
    const body = editBody.trim();
    if (!editingId || !body) return;
    update.mutate({ id: editingId, body }, { onSuccess: () => setEditingId(null) });
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
          Baza wiedzy PRZ-ARON
          {notes.data && (
            <span className="ml-2 text-sm font-normal text-[var(--color-text-secondary)]">
              {activeCount} aktywnych / {items.length}
            </span>
          )}
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
          Opisz tekstowo problem lub sugestię doboru pozycji do wyceny (np. „montaż złącza
          licz zawsze z zarobieniem końców”, „środek transportu do układania kabli/rur to
          samochód dostawczy typu bus”). Aktywne notatki AI uwzględnia przy każdej wycenie:
          przy odczycie pozycji, dopasowaniu nakładów do cennika, wycenie rynkowej, doborze
          podwykonawców i audycie.
        </p>
      </div>

      <div className="card p-4 space-y-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          placeholder="Nowa wskazówka dla AI…"
          className="w-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1.5 text-[var(--color-text-primary)]"
        />
        <div className="flex items-center gap-2">
          <Button onClick={add} disabled={!draft.trim() || create.isPending} loading={create.isPending}>
            Dodaj notatkę
          </Button>
          {create.isError && (
            <span className="text-xs text-red-500">Nie udało się zapisać notatki.</span>
          )}
        </div>
      </div>

      <ul className="card divide-y divide-[var(--color-border)] px-4">
        {notes.isLoading && (
          <li className="py-3 text-sm text-[var(--color-text-secondary)]">Wczytywanie…</li>
        )}
        {!notes.isLoading && items.length === 0 && (
          <li className="py-3 text-sm text-[var(--color-text-secondary)]">
            Brak notatek — dodaj pierwszą wskazówkę powyżej.
          </li>
        )}
        {items.map((n) => (
          <li key={n.id} className="py-3 space-y-1">
            {editingId === n.id ? (
              <div className="space-y-2">
                <textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={3}
                  className="w-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1.5 text-[var(--color-text-primary)]"
                />
                <div className="flex gap-2">
                  <Button onClick={saveEdit} disabled={!editBody.trim() || update.isPending}>
                    Zapisz
                  </Button>
                  <button
                    className="text-xs text-[var(--color-text-secondary)] underline"
                    onClick={() => setEditingId(null)}
                  >
                    Anuluj
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p
                  className={`text-sm whitespace-pre-wrap ${
                    n.is_active
                      ? "text-[var(--color-text-primary)]"
                      : "text-[var(--color-text-muted)] line-through"
                  }`}
                >
                  {n.body}
                </p>
                <div className="flex items-center gap-3 text-[11px] text-[var(--color-text-muted)]">
                  <span>
                    {n.created_by_name} · {new Date(n.created_at).toLocaleDateString("pl-PL")}
                  </span>
                  <button
                    className="underline hover:text-[var(--color-primary)]"
                    onClick={() => update.mutate({ id: n.id, is_active: !n.is_active })}
                  >
                    {n.is_active ? "Wyłącz" : "Włącz"}
                  </button>
                  <button
                    className="underline hover:text-[var(--color-primary)]"
                    onClick={() => startEdit(n)}
                  >
                    Edytuj
                  </button>
                  <button
                    className="underline hover:text-red-500"
                    onClick={() => {
                      if (window.confirm("Usunąć tę notatkę?")) remove.mutate(n.id);
                    }}
                  >
                    Usuń
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
